/// <amd-module name="Repos/Views/FilesHub/Components/FilesHub" />
import * as React from "react";
import { DiffViewerOrientation } from "Repos/Common/Contracts/VersionControlViewModel";
import { getGitRepository } from "Repos/Common/VCViewModelDataProviderService";
import { IFolderContents, IGitItem } from "Repos/Files/Contracts";
import { FileExplorerTree } from "Repos/FilesHubCommon/Components/FileExplorerTree";
import { ReposMessages } from "Repos/Messages/Components/ReposMessages";
import * as Resources from "Repos/Views/FilesHub/Resources";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { PerformanceScenario, PerformanceScenarioContext } from "VSS/Platform/Components/Performance";
import { IVssPageContext } from "VSS/Platform/Context";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { Components, css, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { tryGetLocalStorageValue, trySetLocalStorageValue } from "VSS/Platform/Util/Storage";
import { parseQueryString, Uri } from "VSS/Platform/Util/Url";
import { Observer } from "VSSUI/Observer";
import { Page } from "VSSUI/Page";
import { Splitter, SplitterElementPosition } from "VSSUI/Splitter";
import { Surface, SurfaceBackground } from "VSSUI/Surface";
import { TabBar, TabContent } from "VSSUI/Tabs";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { getFilesHubTabsService, getFilesHubViewService } from "../FilesHubViewService";
// Add an explicit reference to our tabs in order to get them to
// appear in the bundle *before* this module. Otherwise tabs may
// not be registered by the time we go to render them.
import "./BlameContent";
import "./CompareContent";
import "./FileContent";
import { FilesHeader } from "./FilesHeader";
import "./FolderListing";
import "./HighlightChanges";
import "./HistoryContent";
import "./InvalidPath";
import "./PreviewContent";
import { ObservableLike } from "VSS/Core/Observable";

const splitterSettingsKey = "Repos.FilesHub.SplitterWidth";

class FilesHub extends VssComponent<{}> {
    public render() {
        // Handle hash parameters that older links still use (rather than query parameters). If provided,
        // convert them to query parameters and FPS to reload the page.
        const url = new Uri(window.location.href);
        if (url.hashString) {
            const hashParameters = parseQueryString(url.hashString);
            for (const param of hashParameters) {
                url.addQueryParam(param.name, param.value);
            }
            url.hashString = "";
            FastPageSwitch(this.context.pageContext, url.absoluteUri, false);
            return null;
        }

        return (
            <PerformanceScenario area="Repos" scenarioName="files-hub" tti={true}>
                <FilesHubContent pageContext={this.context.pageContext} />
            </PerformanceScenario>
        );
    }
}

const FilesHubContent = (props: { pageContext: IVssPageContext }): JSX.Element => {
    const { pageContext } = props;

    const tabsService = getFilesHubTabsService(pageContext);
    const filesService = getFilesHubViewService(pageContext);
    const layoutManager = pageContext.getService<IVssLayoutManager>("IVssLayoutManager");

    const onSelectedTabChanged = (newTabId: string) => {
        tabsService.setSelectedTabId(newTabId);
    };

    const onRenderNearElement = () => {
        return <FileExplorerTree filesService={filesService} />;
    };

    const onRenderFarElement = () => {
        const selectedTabId = tabsService.getSelectedTabId();
        const tabs = tabsService.getTabs();

        return (
            <Page className="repos-files-hub-page flex-grow custom-scrollbar scroll-auto-hide" tabProviders={tabs} selectedTabId={selectedTabId}>
                <FilesHeader pageContext={pageContext} />
                <TabBar selectedTabId={selectedTabId} onSelectedTabChanged={onSelectedTabChanged} />
                <div className={css("page-content page-content-top flex-column flex-noshrink flex-grow rhythm-vertical-16")}>
                    <ReposMessages className="flex-noshrink" />
                    <FilesHubTabContent pageContext={pageContext} />
                </div>
            </Page>
        );
    };
    return (
        <Observer loadedFolders={filesService.getLoadedFolders()} fullScreenMode={layoutManager.getFullScreenMode()}>
            {(displayProps: { fullScreenMode: boolean, loadedFolders: IFolderContents[] }) => (

                <Surface background={SurfaceBackground.neutral}>
                    {!displayProps.loadedFolders?.length ? (
                        <Page className="flex-grow">
                            <MessageCard severity={MessageCardSeverity.Error}>{Resources.BranchNotFound}</MessageCard>
                        </Page>
                    ) : (
                        <ScreenSizeObserver>
                            {(screenSizeProps: { screenSize: ScreenSize }) => {
                                const isExtraSmallWidth = screenSizeProps.screenSize === ScreenSize.xsmall;
                                if (isExtraSmallWidth) {
                                    filesService.setDiffOrientation(DiffViewerOrientation.Inline);
                                }
                                return (
                                    <Splitter
                                        className={css("scroll-hidden bolt-page-grey", isExtraSmallWidth && "extra-small-view")}
                                        collapsed={filesService.getExplorerCollapsed()}
                                        expandTooltip={Resources.ShowExplorerPane}
                                        farElementClassName="flex-column"
                                        fixedElement={SplitterElementPosition.Near}
                                        initialFixedSize={parseInt("" + tryGetLocalStorageValue(splitterSettingsKey)) || 300}
                                        minFixedSize={180}
                                        nearElementClassName="flex-column"
                                        onCollapsedChanged={collapsed => filesService.setExplorerCollapsed(collapsed)}
                                        onFixedSizeChanged={newSize => {
                                            trySetLocalStorageValue(splitterSettingsKey, "" + newSize);
                                        }}
                                        onRenderFarElement={onRenderFarElement}
                                        onRenderNearElement={isExtraSmallWidth || displayProps.fullScreenMode ? undefined : onRenderNearElement}
                                    />
                                );
                            }}
                        </ScreenSizeObserver>
                    )}
                </Surface>
            )}
        </Observer>
    );
};

const FilesHubTabContent = (props: { pageContext: IVssPageContext }): JSX.Element => {
    const { pageContext } = props;

    const filesService = getFilesHubViewService(pageContext);
    const tabsService = getFilesHubTabsService(pageContext);

    const selectedItem = filesService.getSelectedItem();
    const selectedTab = tabsService.getSelectedTabId();

    let currentItemPath = selectedItem.value.path;
    let currentTab = selectedTab.value;

    return (
        <PerformanceScenarioContext.Consumer>
            {scenarioProps => {
                return (
                    <Observer selectedItem={selectedItem} selectedTab={selectedTab}>
                        {(selectionProps: { selectedItem: IGitItem; selectedTab: string }) => {
                            const newPath = selectionProps.selectedItem.path !== currentItemPath;
                            const newTab = selectionProps.selectedTab !== currentTab;

                            if (newPath || newTab) {
                                const newScenario = scenarioProps.resetScenario()!;
                                newScenario.addProperties({ inPageNavigate: true });
                                newScenario.addProperties({ switchFiles: newPath });
                                newScenario.addProperties({ switchTabs: newTab });

                                currentItemPath = selectionProps.selectedItem.path;
                                currentTab = selectionProps.selectedTab;
                            }

                            scenarioProps.getScenario()!.addProperties({ tab: selectionProps.selectedTab });

                            const navService = pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
                            navService.setPageTitle(
                                selectionProps.selectedItem.path === "/" ? getGitRepository(pageContext)!.name : selectionProps.selectedItem.name
                            );

                            return <TabContent />;
                        }}
                    </Observer>
                );
            }}
        </PerformanceScenarioContext.Consumer>
    );
};

Components.add("ms.vss-code-web.files-hub", FilesHub);
